import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/components/auth';
import { apiClient } from '@/api/client';
import type { SuccessResponse, PaginatedResponse } from '@/types';
import type {
  LessonCard as BaseLessonCard,
  QuestionItem as BaseQuestionItem,
  LearningProgress,
} from '@/types/learning.types';

/**
 * ============================================
 * Private Task Types (Authenticated API) - V2
 * ============================================
 */

/**
 * Task Status
 */
export type TaskStatus = 'active' | 'completed' | 'archived';

/**
 * Task Subject (only Math and English supported)
 */
export type TaskSubject = 'Math' | 'English';

/**
 * Lesson Card (with extra fields)
 */
export interface LessonCard extends BaseLessonCard {
  taskId: string;
  createdAt: string;
}

/**
 * Question Item (with extra fields)
 */
export interface QuestionItem extends BaseQuestionItem {
  taskId: string;
  sortOrder: number;
  createdAt: string;
}

/**
 * Task Progress (with extra fields)
 */
export interface TaskProgress extends LearningProgress {
  lastAccessedAt?: string;
}

/**
 * Task List Item (from GET /tasks)
 */
export interface TaskListItem {
  id: string;
  userId: string;
  conversationId: string | null;
  publicId: string | null;
  shareUrl: string | null;
  subject: string;
  topicLabel: string;
  learningGoal: string | null;
  status: TaskStatus;
  progress: TaskProgress | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Full Task (from GET /tasks/:id) - V2 with unified questions
 */
export interface FullTask extends TaskListItem {
  lessonCard: LessonCard | null;
  questions: QuestionItem[]; // V2: Unified questions
}

/**
 * Task List Query Options
 */
export interface TaskListQueryOptions {
  page?: number;
  pageSize?: number;
  status?: TaskStatus;
  childId?: string;
  subject?: TaskSubject;
}

/**
 * ============================================
 * API Functions
 * ============================================
 */

/**
 * Fetch task list (authenticated endpoint)
 */
async function fetchTaskList(
  options: TaskListQueryOptions = {}
): Promise<PaginatedResponse<TaskListItem>> {
  const params = new URLSearchParams();
  if (options.page) params.append('page', String(options.page));
  if (options.pageSize) params.append('pageSize', String(options.pageSize));
  if (options.status) params.append('status', options.status);
  if (options.childId) params.append('childId', options.childId);
  if (options.subject) params.append('subject', options.subject);

  const queryString = params.toString();
  const url = `/tasks${queryString ? `?${queryString}` : ''}`;

  const { data } = await apiClient.get<PaginatedResponse<TaskListItem>>(url);
  return data;
}

/**
 * Fetch task by ID (authenticated endpoint)
 */
async function fetchTask(taskId: string): Promise<SuccessResponse<FullTask>> {
  const { data } = await apiClient.get<SuccessResponse<FullTask>>(`/tasks/${taskId}`);
  return data;
}

/**
 * Answer history item (from authenticated API)
 */
export interface TaskAnswerHistoryItem {
  id: string;
  questionId: string;
  selectedIndex: number;
  isCorrect: boolean;
  retryCount: number;
  timeSpentMs: number | null;
  answeredAt: string;
}

/**
 * Fetch task answer history (authenticated endpoint)
 */
async function fetchTaskAnswers(taskId: string): Promise<SuccessResponse<TaskAnswerHistoryItem[]>> {
  const { data } = await apiClient.get<SuccessResponse<TaskAnswerHistoryItem[]>>(
    `/tasks/${taskId}/answers`
  );
  return data;
}

/**
 * ============================================
 * Query Keys
 * ============================================
 */

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (options: TaskListQueryOptions) => [...taskKeys.lists(), options] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (taskId: string) => [...taskKeys.details(), taskId] as const,
  answers: (taskId: string) => [...taskKeys.detail(taskId), 'answers'] as const,
};

/**
 * ============================================
 * Hooks
 * ============================================
 */

/**
 * Hook for fetching task list (authenticated endpoint)
 */
export function useTaskList(options: TaskListQueryOptions = {}) {
  const { canMakeAuthenticatedRequests } = useAuthContext();

  return useQuery({
    queryKey: taskKeys.list(options),
    queryFn: () => fetchTaskList(options),
    staleTime: 2 * 60 * 1000,
    enabled: canMakeAuthenticatedRequests,
  });
}

/**
 * Hook for fetching task list with infinite scroll (authenticated endpoint)
 */
export function useInfiniteTaskList(
  options: Omit<TaskListQueryOptions, 'page'> = {}
) {
  const { canMakeAuthenticatedRequests } = useAuthContext();
  const pageSize = options.pageSize ?? 20;

  return useInfiniteQuery({
    queryKey: [...taskKeys.lists(), 'infinite', options] as const,
    queryFn: ({ pageParam = 1 }) =>
      fetchTaskList({ ...options, page: pageParam, pageSize }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.meta;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 2 * 60 * 1000,
    enabled: canMakeAuthenticatedRequests,
  });
}

/**
 * Hook for fetching a task by ID (authenticated endpoint)
 */
export function useTask(taskId: string, options?: { enabled?: boolean }) {
  const { canMakeAuthenticatedRequests } = useAuthContext();

  return useQuery({
    queryKey: taskKeys.detail(taskId),
    queryFn: () => fetchTask(taskId),
    enabled: !!taskId && canMakeAuthenticatedRequests && options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Hook for fetching task answer history (authenticated endpoint)
 *
 * Use this for the parent report page to display:
 * - Each question's correctness
 * - Retry counts
 * - Time spent per question
 */
export function useTaskAnswers(taskId: string, options?: { enabled?: boolean }) {
  const { canMakeAuthenticatedRequests } = useAuthContext();

  return useQuery({
    queryKey: taskKeys.answers(taskId),
    queryFn: () => fetchTaskAnswers(taskId),
    enabled: !!taskId && canMakeAuthenticatedRequests && options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
